import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import { FaUsers } from 'react-icons/fa';
import {
  YBarChart,
  buildMemberActivityChartData,
  memberActivityChartHeight
} from '../../../ui/charts';

export function ClubMemberActivityChart({
  clubStats,
  statsLoading = false,
  variant = 'section',
  className = '',
  dataTestId = 'club-member-activity-chart'
}) {
  const memberActivityData = useMemo(
    () => buildMemberActivityChartData(clubStats?.member_stats),
    [clubStats]
  );
  const memberActivityHeight = useMemo(
    () => memberActivityChartHeight(memberActivityData.length, variant === 'embedded' ? { max: 260 } : undefined),
    [memberActivityData.length, variant]
  );
  const valueFormatter = useCallback((value, name) => {
    if (value == null) return '--';
    const count = Number(value);
    if (Number.isNaN(count)) return '--';
    if (name === 'Hunts') return `${count} hunt${count === 1 ? '' : 's'}`;
    if (name === 'Harvests') return `${count} harvest${count === 1 ? '' : 's'}`;
    return String(count);
  }, []);

  const chartBody = statsLoading && !clubStats ? (
    <div className="text-center py-4">
      <Spinner animation="border" size="sm" className="me-2" />
      <span className="text-muted small">Loading member activity…</span>
    </div>
  ) : memberActivityData.length > 0 ? (
    <YBarChart
      data={memberActivityData}
      height={memberActivityHeight}
      layout="vertical"
      categoryKey="name"
      bars={[
        { dataKey: 'hunts', name: 'Hunts' },
        { dataKey: 'harvests', name: 'Harvests' }
      ]}
      barGap={2}
      barSize={8}
      tooltipLabelFormatter={(label) => label}
      tooltipValueFormatter={valueFormatter}
      dataTestId={dataTestId}
    />
  ) : (
    <div className="text-center py-4" data-testid="club-member-activity-empty">
      <p className="text-muted mb-0">
        <FaUsers className="me-2" aria-hidden />
        No member hunt activity yet
      </p>
    </div>
  );

  if (variant === 'embedded') {
    return (
      <div
        className={`club-inwoods-member-activity ${className}`.trim()}
        data-testid="club-overview-member-activity"
      >
        <div className="club-detail-section__subhead">Member activity</div>
        <p className="club-inwoods-member-activity__hint small text-muted mb-3">
          Hunts &amp; harvests — current season
        </p>
        <div className="club-stats-panel__chart-well">{chartBody}</div>
      </div>
    );
  }

  return (
    <div className={`club-detail-section mb-4 ${className}`.trim()}>
      <div className="club-detail-section__kicker">
        <FaUsers aria-hidden />
        <span>Member activity</span>
      </div>
      <p className="club-detail-section__subhead mb-3">Hunts &amp; harvests — current season</p>
      <div className="club-stats-panel__chart-well">{chartBody}</div>
    </div>
  );
}

ClubMemberActivityChart.propTypes = {
  clubStats: PropTypes.object,
  statsLoading: PropTypes.bool,
  variant: PropTypes.oneOf(['section', 'embedded']),
  className: PropTypes.string,
  dataTestId: PropTypes.string
};

export default ClubMemberActivityChart;
